import Head from 'next/head';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Footer from '../components/Footer';
import blogSlugs from '../data/blogSlugs';
import podcastSlugs from '../data/podcastSlugs';
import projectSlugs from '../data/projectSlugs';
import { generateWebPageSchema } from '../utils/generateWebPageSchema';

export default function Sitemap() {
  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}/sitemap`;

  const formatName = (path) => {
    if (!path || path === '/') return 'Home';
    return path
      .split('/')
      .filter(Boolean)
      .slice(-1)[0]
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const staticPages = [
    '',
    '/services',
    '/projects',
    '/blog',
    '/podcast',
    '/about-us',
    '/contact',
    '/financing',
    '/service-areas',
    '/privacy-policy',
    '/privacy-security',
    '/terms-of-service',
  ];

  const serviceRoutes = [
    '/services/complete-home-remodeling',
    '/services/kitchen-remodeling',
    '/services/bathroom-remodeling',
    '/services/adu',
    '/services/exterior-painting',
    '/services/earthquake-retrofit',
    '/services/foundations',
    '/services/pavers',
    '/services/outdoor-living',
    '/services/gutter',
    '/services/energy-efficient-windows',
    '/services/solar-panels',
    '/services/artificial-turf',
    '/services/hvac',
    '/services/asphalt-shingles',
    '/services/tile-roof',
    '/services/flat-roof',
    '/services/metal-roof',
    '/services/tpo-roof',
  ];

  const serviceAreaRoutes = serviceRoutes.map(
    (route) => `/service-areas/${route.split('/').pop()}`
  );

  const blogPages = blogSlugs.map((slug) => `/blog/${slug}`);
  const podcastPages = podcastSlugs.map((slug) => `/podcast/${slug}`);
  const projectPages = projectSlugs.map((slug) => `/projects/${slug}`);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Sitemap", "item": pageUrl }
    ]
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Modern Build Sitemap Index",
    "itemListElement": [
      // Static Pages
      ...staticPages.map((page, i) => ({
        "@type": "SiteNavigationElement",
        "position": i + 1,
        "name": formatName(page),
        "url": `${baseUrl}${page}`
      })),
      // Service Pages
      ...serviceRoutes.map((page, i) => ({
        "@type": "SiteNavigationElement",
        "position": staticPages.length + i + 1,
        "name": formatName(page),
        "url": `${baseUrl}${page}`
      })),
      // Service Area Pages
      ...serviceAreaRoutes.map((page, i) => ({
        "@type": "SiteNavigationElement",
        "position": staticPages.length + serviceRoutes.length + i + 1,
        "name": formatName(page),
        "url": `${baseUrl}${page}`
      })),
      // Blog Pages
      ...blogPages.map((page, i) => ({
        "@type": "SiteNavigationElement",
        "position": staticPages.length + serviceRoutes.length + serviceAreaRoutes.length + i + 1,
        "name": formatName(page),
        "url": `${baseUrl}${page}`
      })),
      // Podcast Pages
      ...podcastPages.map((page, i) => ({
        "@type": "SiteNavigationElement",
        "position": staticPages.length + serviceRoutes.length + serviceAreaRoutes.length + blogPages.length + i + 1,
        "name": formatName(page),
        "url": `${baseUrl}${page}`
      })),
      // Project Pages
      ...projectPages.map((page, i) => ({
        "@type": "SiteNavigationElement",
        "position": staticPages.length + serviceRoutes.length + serviceAreaRoutes.length + blogPages.length + podcastPages.length + i + 1,
        "name": formatName(page),
        "url": `${baseUrl}${page}`
      })),
    ]
  };

  return (
    <>
    <Head>
      <title>Sitemap | Modern Build</title>
      <meta name="title" content="Sitemap | Modern Build" />
      <meta name="robots" content="index, follow" />
      <meta name="description" content="Browse all Modern Build pages including services, projects, blog posts, podcasts, and service area navigation by location." />
        <link rel="canonical" href={`${baseUrl}/sitemap`} />

      <meta name="author" content="Modern Build Home Remodeling" />

      {/* Open Graph */}
      <meta property="og:title" content="Sitemap | Modern Build" />
      <meta property="og:description" content="Browse all Modern Build pages including services, projects, blog posts, podcasts, and service area navigation by location." />
      <meta property="og:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
      <meta property="og:url" content="https://modernbuildremodel.com/sitemap" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Modern Build Home Remodeling" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Sitemap | Modern Build" />
      <meta name="twitter:description" content="Browse all Modern Build pages including services, projects, blog posts, podcasts, and service area navigation by location." />
      <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-home-remodling-contractor-in-los-angeles.webp" />
      <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Sitemap | Modern Build',
                description: 'Browse all Modern Build pages including services, projects, blog posts, podcasts, and service area navigation by location.',
                slug: '/sitemap'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Sitemap ItemList */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />


      </Head>

      <Header />
      <Hero category="sitemap" page="page" hasContentH1={true} />

      <main className="container mx-auto px-4 py-10 max-w-6xl">

        <h1 className="text-3xl font-gilda mb-6">Sitemap</h1>

        {/* Static */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">Main Pages</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {staticPages.map((page) => (
              <li key={page}>
                <a href={`${baseUrl}${page}`} className="text-primary hover:underline">
                  {formatName(page)}
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Services */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">Services</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {serviceRoutes.map((page) => (
              <li key={page}>
                <a href={`${baseUrl}${page}`} className="text-primary hover:underline">
                  {formatName(page)}
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Service Areas */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">Service Areas</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {serviceAreaRoutes.map((page) => (
              <li key={page}>
                <a href={`${baseUrl}${page}`} className="text-primary hover:underline">
                  {formatName(page)}
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Blogs */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">Blog</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {blogPages.map((page) => (
              <li key={page}>
                <a href={`${baseUrl}${page}`} className="text-primary hover:underline">
                  {formatName(page)}
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Podcasts */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">Podcast</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {podcastPages.map((page) => (
              <li key={page}>
                <a href={`${baseUrl}${page}`} className="text-primary hover:underline">
                  {formatName(page)}
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Projects */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold mb-3">Projects</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {projectPages.map((page) => (
              <li key={page}>
                <a href={`${baseUrl}${page}`} className="text-primary hover:underline">
                  {formatName(page)}
                </a>
              </li>
            ))}
          </ul>
        </section>

        {/* Local Pages Entry */}
        <section>
          <h2 className="text-xl font-semibold mb-3">Local Service Areas</h2>
          <p className="mb-3 text-gray-600">
            Explore all service areas organized by service and city to find your exact location.
          </p>
          <a href="/sitemap/services" className="text-primary font-semibold hover:underline">
            Browse by Service and City →
          </a>
        </section>

      </main>

      <Footer />
    </>
  );
}